/** @jsx jsx */
import { jsx, Container, Grid, Box } from 'theme-ui';
import { FunctionComponent, useRef, useEffect } from 'react';
import FluidImage from '@codersclan/gatsby-theme-airfleet/src/components/fluidImage';

// Types
import { PostDetails as PostDetailsType } from '../../@types/post';

// Components
import Text, { TEXT_TYPES } from '../../../../components/text';
import Share from '../../../../components/share';
import PostHeader from '../../../../components/postHeader';
import Card from '../../../../components/card';
import ResourcesFeaturedDownload from '../../../../components/resourcesFeaturedDownload';
import TextFrame from '../../../../components/textFrame';
import { getExcerptToCard, parseExcerpt } from '../../../../helpers/general';

// Hooks
import { useWpMetadata } from '../../hooks/wpMetadata.hook';

// Helpers
import { getCategory, getCtaLabel } from '../../../../helpers/postHelper';

// Styles
import { baseStyle } from './style';

function reduceSelector(
  acc: PostDetailsType[],
  { postSelector }: { postSelector: PostDetailsType },
) {
  if (postSelector) {
    acc.push(postSelector);
  }

  return acc;
}

const PostDetails: FunctionComponent<{
  post: PostDetailsType;
  relativePosts: Array<{ postSelector: PostDetailsType }> | undefined;
}> = ({ post, relativePosts }) => {
  const blogContentRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (!blogContentRef.current || typeof window === 'undefined') return;
    const links = blogContentRef?.current?.querySelectorAll('a[href^="#"]');

    const header = document.querySelector('header');
    const rect = header?.getBoundingClientRect();
    const headerHeight = rect?.height || 0;

    function navigateToElement(event: MouseEvent) {
      event.preventDefault();
      const element = event.target as HTMLAnchorElement;
      const selector = element.href.slice(element.href.indexOf('#'));
      const target = document.querySelector(selector);
      if (!target) return;

      window.scrollTo({
        top: (target as HTMLElement).offsetTop - headerHeight,
        behavior: 'smooth',
      });
    }

    links.forEach((link: Element) => {
      (link as HTMLAnchorElement).addEventListener('click', navigateToElement);
    });
  }, [blogContentRef]);

  const {
    titlesSettings: {
      optionsTitles: { postLatestContent },
    },
  } = useWpMetadata();
  const individualRelatedContent =
    post.templateResource?.sections?.relatedContent?.related?.reduce(
      reduceSelector,
      [],
    ) || [];
  const globalRelatedContent = relativePosts?.reduce(reduceSelector, []) || [];
  const relatedContent = [
    ...individualRelatedContent,
    ...globalRelatedContent,
  ].slice(0, 3);

  const showRelatedContent = relatedContent.length > 0;
  const author =
    post.templateResource?.sections?.postAuthor?.teamMemberSelector;
  const resourceFeatured = {
    title: post.templateResource?.sections?.resourcesFeaturedTitle?.title,
    bodyText: post.templateResource?.sections?.resourcesFeaturedText?.content,
    image: post.templateResource?.sections?.resourcesFeaturedImage?.image,
    cta: post.templateResource?.sections?.resourcesFeaturedCta,
  };
  if (resourceFeatured.cta && !resourceFeatured.cta.label) {
    resourceFeatured.cta = undefined;
  }

  return (
    <Box sx={baseStyle.mainContainer}>
      <Container>
        <PostHeader
          title={post.title}
          date={new Date(post.date)}
          author={{
            name: author?.title || 'Kendall Serrano',
            headline: author?.templateTeamMember?.sections?.positionText ?? '-',
            image: author?.templateTeamMember?.sections?.image,
            slug: author?.slug,
          }}
        />
        <Share sx={baseStyle.topShare} />
      </Container>
      {post.featuredImage?.node && (
        <FluidImage {...post.featuredImage?.node} sx={baseStyle.mainImage} />
      )}
      <Container sx={baseStyle.innerContainer}>
        <TextFrame
          content={post.content}
          sx={baseStyle.mainText}
          ref={blogContentRef}
        />
        {/* <Share sx={baseStyle.share} /> */}
        {resourceFeatured.title && (
          <ResourcesFeaturedDownload
            title={resourceFeatured.title}
            bodyText={resourceFeatured.bodyText}
            cta={resourceFeatured.cta}
            image={resourceFeatured.image}
            sx={baseStyle.resourcesFeaturedDownload}
          />
        )}
        {showRelatedContent && [
          <Text
            type={TEXT_TYPES.H2}
            sx={baseStyle.moreTitle}
            key="latest_content"
          >
            {postLatestContent.postLatestContentTitle}
          </Text>,
          <Grid
            columns={[1, 3]}
            sx={baseStyle.moreGrid}
            key="latest_content_grid"
          >
            {relatedContent.map(p =>
              p ? (
                <Card
                  key={p.id}
                  image={p.featuredImage?.node}
                  category={getCategory(p).name}
                  title={p.title}
                  content={getExcerptToCard(p)}
                  cta={{
                    label: getCtaLabel(p),
                    url: p.uri,
                  }}
                />
              ) : null,
            )}
          </Grid>,
        ]}
      </Container>
    </Box>
  );
};

export default PostDetails;
